import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CurrencyService, ENVIRONMENT, EnvironmentLocale } from '@cfp/core';
import { RoadmapPortfolioDetails } from '@cfp/shared/roadmap';
import { ChartAbstract, ChartLoader, ROADMAP_CHART_BASE_YEAR } from '@cfp/shared/ui/chart';
import {
  ButtonGroup,
  ButtonToggle,
  Card,
  Co2Pipe,
  Dropdown,
  Icon,
  InputRadio,
  Link,
  LiveRegionStatusUpdaterModule
} from '@cfp/ui';
import { BreakpointObserver, lightenDarkenColor } from '@cfp/ui/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartDataset, ChartType, TooltipItem } from 'chart.js';
import { AnnotationOptions, LineAnnotationOptions } from 'chartjs-plugin-annotation';
import { CurrencyCodeDTO, RoadmapBuildingDetailsDTO, RoadmapBuildingYearDTO } from 'csr-asset-app';
import { BaseChartDirective } from 'ng2-charts';
import { map } from 'rxjs';
import { PropertyNames } from './property-names';

@Component({
  selector: 'cfp-roadmap-chart',
  templateUrl: './roadmap-chart.html',
  imports: [
    Card,
    TranslateModule,
    Dropdown,
    InputRadio,
    FormsModule,
    ButtonGroup,
    ButtonToggle,
    LiveRegionStatusUpdaterModule,
    Icon,
    BaseChartDirective,
    Link
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoadmapChart<RoadmapDetails extends RoadmapBuildingDetailsDTO | RoadmapPortfolioDetails>
  extends ChartAbstract<RoadmapDetails>
  implements OnInit
{
  public readonly propertyNames = input<PropertyNames[]>();
  public readonly title = input<string>();
  public readonly responsive = input<boolean>();
  public readonly currencyCode = input<CurrencyCodeDTO>();
  public readonly startYear = input<number>();

  protected readonly cumulative = signal<boolean>(false);
  protected readonly chartType = signal<ChartType>('bar');

  protected readonly years = computed(() => {
    const details = this.details();
    if (!details) {
      return [];
    }
    let years = details.years
      .filter((year: RoadmapBuildingYearDTO) => year.year)
      .map((year: RoadmapBuildingYearDTO) => {
        return year.year;
      });
    if (this.shouldAddBaseYear()) {
      years.unshift(ROADMAP_CHART_BASE_YEAR);
    }
    return years;
  });

  protected readonly shouldAddBaseYear = computed(() => {
    const propertyNames = this.propertyNames();
    if (!propertyNames) {
      return false;
    }
    return !this.cumulative() && propertyNames.length === 1 && propertyNames[0] === 'co2Consumption';
  });

  protected readonly chartOptions = computed(() => {
    const chartJsLoaded = this.chartLoader.isLoaded();
    const details = this.details();
    const years = this.years();
    const propertyNames = this.propertyNames();
    if (!chartJsLoaded || !details || !years || !propertyNames) {
      return undefined;
    }

    const shouldAddBaseYear = this.shouldAddBaseYear();

    const plugins = {
      annotation: {
        annotations: []
      },
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          padding: 16,
          boxWidth: 12,
          useBorderRadius: this.borderRadius > 0,
          borderRadius: this.borderRadius,
          color: this.fontColor
        }
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems: TooltipItem<'bar'>[]) => {
            return tooltipItems.map((tooltipItem: TooltipItem<'bar'>) => {
              if (propertyNames[0] === 'co2Consumption' && tooltipItem.label === ROADMAP_CHART_BASE_YEAR.toString()) {
                return this.getBaseYearCo2ConsumptionLabel();
              }
              return tooltipItem.label;
            });
          },
          label: (tooltipItem: TooltipItem<'bar'>) => {
            return this.getFormattedValue(tooltipItem.raw);
          },
          labelColor: (tooltipItem: TooltipItem<'bar'>) => {
            const color = this.getColor(tooltipItem.datasetIndex);
            let backgroundColor = color;

            if (shouldAddBaseYear) {
              backgroundColor =
                tooltipItem.label === ROADMAP_CHART_BASE_YEAR.toString()
                  ? this.getBaseYearColor(backgroundColor, 'backgroundColor')
                  : backgroundColor;
            }
            return {
              borderColor: this.cumulative() ? this.getColorWithOpacity(backgroundColor, '11') : backgroundColor,
              backgroundColor: this.cumulative() ? this.getColorWithOpacity(backgroundColor, '11') : backgroundColor,
              borderWidth: 0,
              borderRadius: this.borderRadius
            };
          }
        }
      },
      datalabels: { display: () => false }
    };
    let chartOptions: ChartConfiguration['options'] | any = Object.assign({}, this.defaultOptions, {
      plugins: plugins
    });

    if (!this.cumulative() && propertyNames[0] === 'co2Consumption') {
      let targetColor = this.secondaryColor;
      if (targetColor === this.primaryCo2Color) {
        targetColor = lightenDarkenColor(targetColor, -40);
      }

      if (this.environment.localeId === EnvironmentLocale.EN_GB) {
        chartOptions.plugins.annotation.annotations = [
          this.createChartAnnotiation(
            details.gbTargetCo2Consumption2035,
            this.translateService.instant('target.2035'),
            this.yearTargetLineTextColor || '#ffffff',
            this.yearTargetLineColor || targetColor
          )
        ];
      }

      if (this.environment.localeId === EnvironmentLocale.NL_NL) {
        chartOptions.plugins.annotation.annotations = [
          this.createChartAnnotiation(
            details.targetCo2Consumption2030,
            this.translateService.instant('target.2030'),
            this.yearTargetLineTextColor || '#ffffff',
            this.yearTargetLineColor || targetColor
          )
        ];
      }

      if (this.showTarget2050) {
        chartOptions.plugins.annotation.annotations = [
          this.createChartAnnotiation(
            details.targetCo2Consumption2050,
            this.translateService.instant('target.2050'),
            this.yearTargetLineTextColor || '#ffffff',
            this.getColorWithOpacity(this.secondaryColor, '88')
          )
        ];
      }
    } else {
      chartOptions.plugins.annotation.annotations = [];

      if (this.cumulative()) {
        chartOptions.plugins.legend.labels = {
          usePointStyle: true,
          padding: 16,
          generateLabels: () => {
            return propertyNames.map((propertyName: string, index: number) => {
              const color = this.getColor(index);
              return {
                text: this.translateService.instant('cumulative.' + propertyName),
                fontColor: this.fontColor,
                lineWidth: 2,
                strokeStyle: color,
                pointStyle: 'line'
              };
            });
          }
        };
      }
    }

    if (shouldAddBaseYear) {
      chartOptions.plugins.legend.labels.generateLabels = () => {
        return [
          {
            text: this.getBaseYearCo2ConsumptionLabel(),
            fontColor: this.fontColor,
            fillStyle: this.baseYearColor || this.getBaseYearColor(this.primaryColor),
            borderColor: this.baseYearColor || this.getBaseYearColor(this.primaryColor),
            datasetIndex: 0,
            pointStyle: false,
            lineWidth: 0,
            borderRadius: this.borderRadius
          },
          {
            text: this.translateService.instant('roadmap.yearlyCo2Consumption'),
            fontColor: this.fontColor,
            fillStyle: this.primaryCo2Color || this.primaryColor,
            borderColor: this.primaryCo2Color || this.primaryColor,
            datasetIndex: 0,
            pointStyle: false,
            lineWidth: 0,
            borderRadius: this.borderRadius
          }
        ];
      };
    }

    return chartOptions;
  });

  protected readonly chartData = computed(() => {
    const chartJsLoaded = this.chartLoader.isLoaded();
    const details = this.details();
    const years = this.years();
    const propertyNames = this.propertyNames();
    if (!chartJsLoaded || !details || !years || !propertyNames) {
      return undefined;
    }

    const shouldAddBaseYear = this.shouldAddBaseYear();
    return {
      labels: years,
      datasets: propertyNames.map((propertyName: PropertyNames, index: number) => {
        const color = this.getColor(index);
        let cumulativeValue = 0;
        let data = details.years
          .filter((year: RoadmapBuildingYearDTO) => year.year)
          .map((year: RoadmapBuildingYearDTO) => {
            let value = year[propertyName] || 0;

            if (propertyName === 'investment' || propertyName === 'costReduction') {
              value = this.currencyService.convertToUserCurrency(value, this.currencyCode()!);
            }

            if (this.cumulative()) {
              cumulativeValue += value;
              value = cumulativeValue;
            }

            return value;
          });

        let backgroundColor: string | string[] = this.cumulative() ? this.getColorWithOpacity(color, '11') : color;
        let hoverBackgroundColor: string | string[] = lightenDarkenColor(color, -20) + (this.cumulative() ? '11' : '');
        let borderColor: string | string[] = color;
        let hoverBorderColor: string | string[] = lightenDarkenColor(color, -60);

        if (shouldAddBaseYear) {
          data.unshift(details.co2ConsumptionBaseYear);

          backgroundColor = years.map((year: number) => {
            return year === ROADMAP_CHART_BASE_YEAR ? this.getBaseYearColor(color) : color;
          });

          hoverBackgroundColor = years.map((year: number) => {
            return year === ROADMAP_CHART_BASE_YEAR
              ? this.getBaseYearColor(color, 'hoverBackground')
              : lightenDarkenColor(color, -20);
          });

          borderColor = years.map((year: number) => {
            return year === ROADMAP_CHART_BASE_YEAR ? this.getBaseYearColor(color) : color;
          });

          hoverBorderColor = years.map((year: number) => {
            return year === ROADMAP_CHART_BASE_YEAR
              ? this.getBaseYearColor(color, 'hoverBorder')
              : lightenDarkenColor(color, -60);
          });
        }

        let datasets: ChartDataset = {
          label: this.translateService.instant((this.cumulative() ? 'cumulative.' : 'yearly.') + propertyName),
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          hoverBackgroundColor: hoverBackgroundColor,
          hoverBorderColor: hoverBorderColor,
          hoverBorderWidth: 2,
          barPercentage: 0.6,
          data: data,
          pointBorderColor: color,
          cubicInterpolationMode: 'monotone',
          borderRadius: this.borderRadius
        };

        if (this.cumulative()) {
          datasets = Object.assign(datasets, {
            pointStyle: 'circle',
            pointBorderColor: this.secondaryColor,
            pointBackgroundColor: this.secondaryColor,
            pointHoverBorderWidth: 2,
            pointHoverBorderColor: this.secondaryColor,
            pointHitRadius: 10,
            cubicInterpolationMode: 'monotone',
            borderRadius: this.borderRadius,
            fill: 'origin'
          });
        }

        return datasets;
      })
    };
  });

  protected barChartPlugins = [];
  protected readonly showDropdown = toSignal(
    inject(BreakpointObserver)
      .observe()
      .pipe(map((state) => state.breakpoints.xs === true || state.breakpoints.sm === true)),
    { initialValue: false }
  );

  private defaultOptions: ChartConfiguration['options'] = {
    responsive: true,
    resizeDelay: 200,
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false,
          callback: (index: number) => {
            const chartData = this.chartData();
            const propertyNames = this.propertyNames();
            if (!chartData || !propertyNames) {
              return '-';
            }
            const label = chartData.labels[index] as number;
            if (propertyNames[0] === 'co2Consumption' && label === ROADMAP_CHART_BASE_YEAR) {
              const startYear = this.startYear();
              return startYear ? startYear : '-';
            }
            return label;
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          callback: (value: any) => this.getFormattedValue(value)
        }
      }
    },
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: 0
      }
    }
  };

  private showTarget2050 = false;
  private baseYearCo2ConsumptionLabel: string;
  private readonly baseYearColor: string;
  private readonly yearTargetLineColor: string;
  private readonly yearTargetLineTextColor: string;
  private readonly primaryCo2Color: string;
  private readonly borderRadius: number = 3;

  private readonly environment = inject(ENVIRONMENT);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly currencyService = inject(CurrencyService);
  private readonly co2Pipe = inject(Co2Pipe);
  private readonly translateService = inject(TranslateService);
  private readonly chartLoader = inject(ChartLoader);

  public constructor() {
    super();

    const style = getComputedStyle(document.body);
    this.baseYearColor = style.getPropertyValue('--chart-base-year').trim();
    this.yearTargetLineColor =
      style.getPropertyValue('--chart-average').trim() || style.getPropertyValue('--bs-secondary');
    this.yearTargetLineTextColor =
      style.getPropertyValue('--chart-average-text').trim() || style.getPropertyValue('--cfp-white');
    this.primaryCo2Color = (
      style.getPropertyValue('--cfp-partner-styling-primary') ||
      style.getPropertyValue('--chart-primary-co2') ||
      style.getPropertyValue('--bs-primary')
    ).trim();

    const borderRadius = style.getPropertyValue('--chart-radius').trim();
    if (borderRadius) {
      this.borderRadius = +borderRadius;
    }

    if (this.primaryColor === this.secondaryColor) {
      this.secondaryColor = this.getColorWithOpacity(this.secondaryColor, '22');
    }
  }

  public ngOnInit(): void {
    this.chartLoader.load();
  }

  protected setCumulative(cumulative: boolean): void {
    this.cumulative.set(cumulative);
    this.chartType.set(cumulative ? 'line' : 'bar');
  }

  protected getTranslation(key: string): string | undefined {
    const text = this.translateService.instant(key);
    if (text !== key) {
      return text;
    }

    return undefined;
  }

  private getBaseYearCo2ConsumptionLabel(): string {
    if (!this.baseYearCo2ConsumptionLabel) {
      this.baseYearCo2ConsumptionLabel = this.translateService.instant('roadmap.baseYearCo2Consumption');
    }
    return this.baseYearCo2ConsumptionLabel;
  }

  protected getFormattedValue(value: any): string | undefined {
    const propertyNames = this.propertyNames();
    if (propertyNames && (propertyNames[0] === 'co2ConsumptionReduction' || propertyNames[0] === 'co2Consumption')) {
      return this.co2Pipe.transform(value, false, true, false, false);
    } else {
      return (
        this.currencyPipe.transform(
          value,
          this.currencyService.getUserCurrencyCode(this.currencyCode()!),
          this.environment.currencyDisplay || 'symbol-narrow',
          '1.0-0'
        ) || undefined
      );
    }
  }

  private createChartAnnotiation(
    value: number,
    label: string,
    targetTextColor: string,
    targetColor: string
  ): AnnotationOptions & LineAnnotationOptions {
    return {
      type: 'line',
      scaleID: 'y',
      value: value,
      borderColor: targetColor,
      borderWidth: 2,
      label: {
        display: true,
        content: label,
        position: 'end',
        xAdjust: 6,
        color: targetTextColor,
        backgroundColor: targetColor,
        borderRadius: this.borderRadius
      }
    };
  }

  protected showData(): void {
    const chartData = this.chartData();
    if (!chartData) {
      return;
    }
    this.showDataEvent.emit(
      super.transformYearChartDataToTableData(
        chartData,
        this.translateService.instant('year'),
        this.translateService.instant(this.title() || this.propertyNames()?.[0] || ''),
        this.getBaseYearCo2ConsumptionLabel()
      )
    );
  }

  private getBaseYearColor(color: string, type?: string): string {
    if (type === 'hoverBackground') {
      return this.baseYearColor ? lightenDarkenColor(this.baseYearColor, -20) : this.getColorWithOpacity(color, '44');
    } else if (type === 'hoverBorderColor') {
      return this.baseYearColor ? lightenDarkenColor(this.baseYearColor, -60) : this.getColorWithOpacity(color, '44');
    } else {
      return this.baseYearColor || this.getColorWithOpacity(color, '22');
    }
  }

  private getColor(index: number): string {
    if (index === 0) {
      const propertyNames = this.propertyNames();
      if (propertyNames && propertyNames[index] === 'co2Consumption') {
        return this.primaryCo2Color || this.primaryColor;
      } else if (propertyNames && propertyNames[index] === 'co2ConsumptionReduction') {
        return this.primaryCo2Color || this.secondaryColor;
      } else {
        return this.primaryColor;
      }
    } else {
      return this.secondaryColor;
    }
  }
}
